<template>

<div id="partner-profile-wrapper">

  <!-- Available language options for profile data -->
  <div id="profile-languages">
    <button class="profile-languages-button" 
      :class="{ 'profile-language-selected': language.rel === $route.params.language }" 
      v-for="language in languages" :key="language.href" 
      @click="setLanguage(language.rel)">{{ languageNames[language.rel] }}</button>
  </div>

  <div id="partner-profile-container">

    <BackComponent />

    <LoaderComponent v-if="!loaded" />
    <div v-else id="partner-profile">

      <p id="partner-name">{{ info.partnerName }}</p>
      <p id="partner-location"><span v-if="info.city">{{ info.city }}, </span>{{ info.countryName }}</p>

      <!-- "Links" to show different categories of information -->
      <div id="partner-links-container">
        <div id="partner-links-wrapper">
          <div id="partner-links" class="partner-links-background">
            <div>
              <button @click="setShowingInfo = 'description'">About</button>
              <span class="partner-links-divider">|</span>
              <button @click="setShowingInfo = 'contact'" v-if="hasContactInfo">Contact</button>
              <span class="partner-links-divider" v-if="hasContactInfo">|</span>
              <button @click="setShowingInfo = 'logo'">Logo</button>
              <span class="partner-links-divider" v-if="info.url && !lessThan599">|</span>
            </div>
            <div>
              <a id="partner-home" v-if="info.url" :href="sanitizedURL" target="_blank">
                <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> Go to the Partner's homepage
              </a>
            </div>
          </div>
        </div>
        <!-- Link that goes to Partner Objects page -->
        <div id="partner-objects" v-if="info.hasObjects">
          <router-link class="partner-links-background" :to="{ 
            name: 'partner-objects', 
            params: { database: info.projectId, 
              country: info.countryId, 
              id: info.partnerId, 
              page: 1 }}">View Objects
          </router-link>
        </div>
      </div>

      <div id="profile-photo-wrapper">
        
        <!-- Partner photos and thumbnails -->
        <div class="profile-photo-container" v-if="info.pictures.length">
          <div class="profile-photo" @click="showLightbox = true">
            <ZoomComponent :img-normal="currentPhoto"></ZoomComponent>
          </div>
          <div id="profile-thumbnail-container">
            <div class="profile-thumbnail" v-for="pic in info.pictures" :key="pic.imagePath" @click="currentPhoto = pic.L">
              <img :src="pic.L" />
              <!-- Hover information for photo -->
              <div class="tooltip-text" v-if="(pic.photographer || pic.copyright || pic.caption) && !lessThan974">
                <div v-if="pic.photographer">{{ $t("photograph") }}: {{ pic.photographer }}</div>
                <div v-if="pic.copyright">&#169; {{ pic.copyright }}</div>
                <div v-if="pic.caption">{{ pic.caption }}</div>
              </div>
            </div>
          </div>
          <!-- Full-page lightbox for photo "slideshow" -->
          <div id="lightbox-container" v-if="showLightbox" @click="showLightbox = false">
            <div id="lightbox">
              <img :src="currentPhoto" />
            </div>
            <div id="lightbox-left" class="lightbox-control" v-if="info.pictures.length > 1">
              <button @click.stop="slideshow('left')"><font-awesome-icon icon="fa-solid fa-chevron-left" /></button>
            </div>
            <div id="lightbox-right" class="lightbox-control" v-if="info.pictures.length > 1">
              <button @click.stop="slideshow('right')"><font-awesome-icon icon="fa-solid fa-chevron-right" /></button>
            </div>
          </div>
        </div>
        <!-- Placeholder for layout spacing when there is no photo information -->
        <div class="profile-photo-container" v-else></div>

        <!-- Contains sets of information to show based on selection from "links" -->
        <div id="profile-info-container">
          <div v-if="setShowingInfo === 'description'"
            v-html="info.description"
            :dir="rightToLeft ? 'rtl' : 'ltr'">
          </div>
          <div v-if="setShowingInfo === 'contact'">
            <p id="address-header">Address(es)</p>
            <p v-html="info.address"></p>
            <p v-if="info.phone">T {{ info.phone }}</p>
            <p v-if="info.fax">F {{ info.fax }}</p>
            <p v-if="info.email">
              <a :href="'mailto:' + info.email">{{ info.email }}</a>
            </p>
            <p v-if="info.title">{{ info.title }}</p>
            <p id="partner-url" v-if="info.url">
              <a :href="sanitizedURL" target="_blank">{{ info.url }}</a>
            </p>
            <div>
              <!-- Partner contact information (may be more than one person) -->
              <div class="contact-person" v-for="person in info.contactPerson" :key="person.name">
                <p class="contact-title" v-if="person.title">{{ person.title }}</p>
                <p v-if="person.name">{{ person.name }}</p>
                <p v-if="person.phone">T {{ person.phone }}</p>
                <p v-if="person.fax">F {{ person.fax }}</p>
                <p v-if="person.mail">
                  <a :href="'mailto:' + person.mail">{{ person.mail }}</a>
                </p>
              </div>
            </div>
          </div>
          <div id="partner-logo-container" v-if="setShowingInfo === 'logo'">
            <img class="logo" 
              v-for="logo in info.logos" :key="logo" 
              :src="logo" />
          </div>
        </div>

      </div>

      <!-- Show label for map if there is map data available -->
      <!-- Actual container for map is below -->
      <p id="profile-map-label" v-if="showMap">Museum on Google Maps</p>
      <p id="profile-no-map" v-else></p>

    </div>
    
  </div>

  <!-- Contains the Google Map for Partner location -->
  <div id="partner-profile-map" v-if="showMap"></div>

</div>

</template>



<script>

import BackComponent from "./BackComponent.vue";
import LoaderComponent from "./LoaderComponent.vue";
import ZoomComponent from "./ZoomComponent.vue";
import media from "../mixins/media.js";
export default {
  name: "PartnerProfile",
  components: {
    BackComponent: BackComponent,
    "LoaderComponent": LoaderComponent,
    "ZoomComponent": ZoomComponent
  },
  mixins: [media],
  computed: {
    hasContactInfo: function() {
      if (this.info.address ||
          this.info.phone ||
          this.info.fax ||
          this.info.email ||
          this.info.title ||
          this.info.url ||
          this.info.contactPerson.length) {
        return true;
      } else {
        return false;
      }
    }
  },
  data() {
    return {
      lessThan974: window.matchMedia("(max-width: 974px)").matches,
      lessThan599: window.matchMedia("(max-width: 599px)").matches,
      loaded: false,
      database: "",
      country: "",
      id: "",
      language: "",
      languages: {},
        languageNames: {},
        rightToLeft: null,
      info: {},
        sanitizedURL: "",
      currentPhoto: "",
      showLightbox: false,
      setShowingInfo: "description",
      showMap: false,
      urls: {
        portal: process.env.VUE_APP_URL_PORTAL,
        baroqueArt: process.env.VUE_APP_URL_DBA,
        islamicArt: process.env.VUE_APP_URL_DIA,
        sharingHistory: process.env.VUE_APP_URL_SH,
        galleries: process.env.VUE_APP_URL_SELF,
        images: process.env.VUE_APP_URL_IMAGES,
      },
    }
  },
  watch: {
    "$route" () {
      this.getPartnerInfo();
    }
  },
  methods: {
    // Prop used to set conditional dir
    setRightToLeft: function(language) {
      if (language === "ar") {
        this.rightToLeft = true;
      } else {
        this.rightToLeft = false;
      }
    },
    // Save full names of languages in that language
    // Used for display
    getLanguageNames: function() {
      return axios({ method: "GET", "url": `${process.env.VUE_APP_DXA_ENDPOINT}/languages` }).then(result => {
        this.languageNames = result.data;
      }, error => {
        console.error(error);
      });
    },
    // Set a new language by changing route parameters
    setLanguage: function(language) {
      this.loaded = false;
      let currentParams = JSON.parse(JSON.stringify(this.$route.params));
      let updateParams = currentParams;
      updateParams["language"] = language;
      this.language = language;
      this.$router.push({ name: "partner", params: updateParams });
    },
    // Main function for this page
    getPartnerInfo: function() {
      this.database = this.$route.params.database;
      this.country = this.$route.params.country;
      this.id = this.$route.params.id;
      this.language = this.$route.params.language;
      this.setRightToLeft(this.language);
      let baseURL = `${process.env.VUE_APP_DXA_ENDPOINT}/`;
      let addURL, fullURL;
      // URL changes depending on associated project for partner
      if (this.database === "AWE") {
        addURL = `sh/partners/${this.id}/${this.language}?hash=${process.env.VUE_APP_API_HASH}`;
      } else {
        addURL = `mwnf3/partners/${this.country}/${this.id}/${this.language}?hash=${process.env.VUE_APP_API_HASH}`;
      }
      fullURL = baseURL + addURL;
      return axios({ method: "GET", "url": fullURL }).then(result => {
        this.languages = result.data.i18nLinks;
        this.info = result.data;
        this.setSanitizedURL(this.info.url);
        if (this.info.pictures.length) {
          this.currentPhoto = result.data.pictures[0].L;
        }

        // Google Map section
        if (this.info.mapCoordinates) {
          this.showMap = true;
        }
        // nextTick necessary as container element must be loaded/exist first
        this.$nextTick(() => {
          // Only run if map coordinates are available
          if (this.info.mapCoordinates) {
            let coordinates = {};
            let coordinatesArray = this.info.mapCoordinates.split(",");
            coordinates.lat = parseFloat(coordinatesArray[0]);
            coordinates.lng = parseFloat(coordinatesArray[1]);
            let zoom = parseFloat(this.info.mapZoom);
            let museumName = this.info.partnerName;
            let museumLocation = `${this.info.city}, ${this.info.countryName}`;
            // Create a new Google Maps object
            this.map = new google.maps.Map(document.getElementById("partner-profile-map"), {
              center: coordinates,
              zoom: zoom,
              mapTypeId: "hybrid",
              styles: [
                {
                  "featureType": "poi",
                  "elementType": "labels.text",
                  "stylers": [
                    {
                      "visibility": "off"
                    }
                  ]
                },
                {
                  "featureType": "poi.business",
                  "stylers": [
                    {
                      "visibility": "off"
                    }
                  ]
                },
                {
                  "featureType": "road",
                  "elementType": "labels.icon",
                  "stylers": [
                    {
                      "visibility": "off"
                    }
                  ]
                },
                {
                  "featureType": "transit",
                  "stylers": [
                    {
                      "visibility": "off"
                    }
                  ]
                }
              ]
            });
            // Add a "marker" to the map
            this.marker = new google.maps.Marker({
              position: coordinates,
              map: this.map,
              icon: require("../assets/pointer.png"),
              title: museumName,
              clickable: true
            });
            // Add a window that displays when clicking the marker
            this.infoWindow = new google.maps.InfoWindow({
              content: `<b><span style="color: #000;">${museumName}<br />${museumLocation}</span></b>`
            });
            // Add an event listener for the window
            this.marker.addListener("click", () => {
              this.infoWindow.open(this.map, this.marker);
            });
          }
        });

        this.loaded = true;
      }, error => {
        this.$router.replace({ name: "error" });
        console.error(error);
      });
    },
    // For slideshow/lightbox navigation
    slideshow: function(clicked) {
      let urls = [];
      for (let i = 0; i < this.info.pictures.length; i++) {
        urls.push(this.info.pictures[i].L);
      }
      let index = urls.indexOf(this.currentPhoto);
      let newIndex;
      if (clicked === "right") {
        if (index === urls.length - 1) {
          newIndex = 0;
        } else {
          newIndex = index + 1;
        }
      } else if (clicked === "left") {
        if (index === 0) {
          newIndex = urls.length - 1;
        } else {
          newIndex = index - 1;
        }
      }
      this.currentPhoto = urls[newIndex];
    },
    // Sanitize the provided Partner URL
    setSanitizedURL: function(url) {
      let http = "http://";
      if (!url) {
        return;
      }
      if (url.search("://") === -1) {
        this.sanitizedURL = http + url;
      } else {
        this.sanitizedURL = url;
      }
    },
  },
  created() {
    this.getPartnerInfo();
    this.getLanguageNames();
  }
}

</script>



<style scoped lang="scss">

#partner-profile-wrapper {
  width: 100%;
  background-color: white;
}

#profile-languages {
  @include flex-center;
  justify-content: flex-end;
  background-color: $theme-dark;
}
  .profile-languages-button {
    @include button-styles;
    color: $light-text;
    font-size: 95%;
    padding: 4px 8px;
  }
    .profile-language-selected {
      background-color: $theme-light;
    }

#partner-profile-container {
  width: 100%;
  background-color: white;
  padding: 0 50px;
}

#back {
  @include flex-center;
  justify-content: flex-start;
  height: 50px;
  padding: 30px 50px 0 0;
}
  #back .fas {
    font-size: 75%;
    padding-right: 4px;
  }
  #back button {
    @include button-styles;
    padding: 2px;
    font-weight: bold;
  }

.loader-container {
  min-height: 500px;
}

#partner-profile {
  padding-top: 20px;
}
  #partner-name {
    font-size: 110%;
  }
  #partner-location {
    font-style: italic;
    font-weight: bold;
  }

#partner-links-container {
  @include flex-center;
  justify-content: flex-start;
  margin: 20px 0;
  font-size: 95%;
  color: $light-text;
}
  #partner-links-container a {
    text-decoration: none;
    color: $light-text;
  }
  #partner-links-wrapper {
    @include flex-center;
    justify-content: flex-start;
    width: 40%;
  }
    #partner-links {
      @include flex-center;
    }
    .partner-links-background {
      background-color: $theme-light;
      border-radius: 50px;
      padding: 2px 12px;
    }
    #partner-links button {
      @include button-styles;
      color: $light-text;
    }
    .partner-links-divider {
      margin: 0 10px;
    }
    #partner-links .fas {
      font-size: 85%;
      padding-right: 5px;
    }
  #partner-objects {
    width: 60%;
    padding-left: 50px;
  }
    #partner-objects a {
      font-weight: bold;
      letter-spacing: 0.5px;
    }

#profile-photo-wrapper {
  @include flex-center;
  align-items: flex-start;
}

.profile-photo-container {
  width: 40%;
}
  .profile-photo {
    width: 100%;
    cursor: zoom-in;
  }
    .profile-photo :deep(.zoom-on-hover .normal) {
      object-fit: cover;
    }
  #profile-thumbnail-container {
    @include flex-center;
    justify-content: flex-start;
  }
    .profile-thumbnail {
      height: 60px;
      width: 60px;
      position: relative;
      margin: 10px 10px 0 0;
      cursor: pointer;
    }
      .profile-thumbnail:hover .tooltip-text {
        visibility: visible;
      }
      .profile-thumbnail img {
        @include image-scale;
        object-fit: cover;
      }
    .tooltip-text {
      @include flex-center;
      flex-direction: column;
      height: 60px;
      position: absolute;
      bottom: 0;
      left: 110%;
      background-color: rgba($theme-dark, 0.95);
      color: $light-text;
      font-size: 90%;
      text-align: center;
      white-space: nowrap;
      padding: 5px 10px;
      border-radius: 6px;
      visibility: hidden;
      pointer-events: none;
      z-index: 60;
    }
      .tooltip-text::after {
        content: "";
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 100%;
        border-width: 5px;
        border-style: solid;
        border-color: transparent rgba($theme-dark, 0.95) transparent transparent;
      }
  #lightbox-container {
    @include flex-center;
    height: 100vh;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 200;
    background-color: rgba(black, 0.9);
  }
    #lightbox {
      height: 90%;
      width: 90%;
    }
      #lightbox img {
        @include image-scale;
        object-fit: contain;
      }
    .lightbox-control {
      position: absolute;
      font-size: 30px;
    }
      .lightbox-control button {
        @include flex-center;
        @include button-styles;
        height: 45px;
        width: 45px;
        border: 2px solid white;
        border-radius: 100%;
        color: white;
      }
      #lightbox-left {
        left: 10px;
      }
      #lightbox-right {
        right: 10px;
      }

#profile-info-container {
  width: 60%;
  padding-left: 50px;
}
  #profile-info-container a {
    @include link-styles;
  }
    #profile-info-container a:hover {
      @include link-hover-styles;
    }
      #address-header {
        font-weight: bold;
      }
      #partner-url {
        padding-top: 10px;
      }
      .contact-person {
        padding-top: 10px;
      }
        .contact-title {
          font-weight: bold;
        }
    #partner-logo-container {
      @include flex-center;
      flex-direction: column;
      align-items: flex-start;
    }
      .logo {
        padding-bottom: 10px;
      }

#profile-map-label {
  font-size: 110%;
  padding: 30px 0 20px 0;
}
#profile-no-map {
  margin-bottom: 100px;
}

#partner-profile-map {
  height: 50vh;
  max-height: 400px;
}

/* IE-only */
@media only screen and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  #lightbox {
    position: relative;
  }
    #lightbox img {
      max-height: 100%;
      max-width: 100%;
      height: auto;
      width: auto;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      margin: auto;
    }
}

@media only screen and (max-width: 1199px) {
  #partner-name {
    font-size: 105%;
  }
  #partner-links {
    flex-direction: column;
    padding: 2px 20px;
  }
  #profile-map-label {
    font-size: 105%;
  }
}

@media only screen and (max-width: 974px) {
  .lightbox-control {
    font-size: 25px;
  }
    .lightbox-control button {
      height: 40px;
      width: 40px;
    }
}

@media only screen and (max-width: 724px) {
  #partner-links-container {
    flex-direction: column;
    align-items: flex-start;
  }
    #partner-links-wrapper {
      width: 100%;
    }
      #partner-links {
        flex-direction: row;
        padding: 2px 12px;
        margin-bottom: 20px;
      }
  #partner-objects {
    width: 100%;
    padding-left: 0;
  }
  #profile-photo-wrapper {
    flex-direction: column;
  }
    .profile-photo-container {
      width: 100%;
      margin-bottom: 30px;
    }
    #profile-info-container {
      width: 100%;
      padding-left: 0;
      margin-bottom: 30px;
    }
}

@media only screen and (max-width: 599px) {
  #partner-profile-container {
    padding: 0 30px;
  }
  .partner-links-background {
    border-radius: 5px;
  }
  #partner-links {
    flex-direction: column;
    padding: 5px 20px; 
  }
    #partner-links div:first-child {
      margin-bottom: 10px;
    }
  #partner-objects a {
    padding: 2px 20px;
  }
}

@media only screen and (max-width: 399px) {
  #partner-links {
    padding: 5px 10px; 
  }
  #partner-objects a {
    padding: 2px 10px;
  }
  
}

</style>